import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Eye, MessageCircle, X } from "lucide-react";
import { apiClient, API } from "@/lib/api";
import { WHATSAPP_URL } from "./data";
import { SectionHeading } from "./Services";
import { scrollToSection } from "@/lib/scrollTo";

const SampleViewer = ({ sample, onClose }) => (
  <AnimatePresence>
    {sample && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[95] flex items-center justify-center bg-[#12294D]/70 p-4 backdrop-blur-sm"
        onClick={onClose}
        data-testid="sample-viewer-modal"
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          transition={{ duration: 0.3, ease: [0.33, 1, 0.68, 1] }}
          className="flex h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-[#A07E1F]/15 px-5 py-4">
            <div>
              <h3 className="font-serif-display text-lg font-semibold text-[#12294D]">{sample.title}</h3>
              <p className="font-mono-accent text-[9px] uppercase tracking-[0.2em] text-[#5E6E85]">
                {sample.subject} · {sample.level} · View only
              </p>
            </div>
            <button onClick={onClose} data-testid="sample-viewer-close" aria-label="Close viewer" className="rounded-full border border-[#A07E1F]/30 p-2 text-[#A07E1F] transition-colors hover:bg-[#D4AF37]/10">
              <X size={16} />
            </button>
          </div>
          <iframe
            src={`${API}/samples/${sample.id}/view`}
            title={sample.title}
            className="w-full flex-1 bg-[#F3EEE2]"
            data-testid="sample-viewer-frame"
          />
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export const Samples = () => {
  const [samples, setSamples] = useState([]);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    apiClient.get("/samples").then((r) => setSamples(r.data || [])).catch(() => {});
  }, []);

  return (
    <section id="samples" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-36" data-testid="samples-section">
      <SectionHeading id="samples" overline="Proof Before Payment" title="Judge our writing" accent="yourself." />
      {samples.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="luxury-card flex flex-col items-start gap-6 rounded-xl p-10 sm:flex-row sm:items-center sm:justify-between"
          data-testid="samples-empty-state"
        >
          <div>
            <h3 className="font-serif-display text-2xl font-semibold text-[#12294D]">Fresh samples are on their way.</h3>
            <p className="mt-2 max-w-xl text-sm text-[#46586F]">
              Message us on WhatsApp with your subject and we'll send a relevant sample paper within minutes.
            </p>
          </div>
          <a
            href={`${WHATSAPP_URL}?text=${encodeURIComponent("Hi! Could you send me a sample paper for my subject?")}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="samples-whatsapp-button"
            className="flex items-center gap-2 rounded-full bg-[#D4AF37] px-7 py-3.5 font-mono-accent text-[11px] font-bold uppercase tracking-[0.18em] text-[#12294D] transition-colors duration-300 hover:bg-[#C9A227]"
          >
            <MessageCircle size={15} /> Request a Sample
          </a>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {samples.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 4) * 0.1, ease: [0.33, 1, 0.68, 1] }}
              className="luxury-card group flex flex-col rounded-xl p-7"
              data-testid={`sample-card-${i}`}
            >
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-lg border border-[#A07E1F]/30 bg-[#D4AF37]/5">
                <FileText size={20} className="text-[#A07E1F]" />
              </div>
              <h3 className="font-serif-display text-xl font-semibold leading-snug text-[#12294D]">{s.title}</h3>
              <p className="mt-3 font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#5E6E85]">
                {s.subject} · {s.level} · {s.pages} pages
              </p>
              <button
                onClick={() => setViewing(s)}
                data-testid={`sample-view-button-${i}`}
                className="mt-8 flex items-center gap-2 self-start font-mono-accent text-[11px] uppercase tracking-[0.2em] text-[#A07E1F] transition-colors duration-300 hover:text-[#12294D]"
              >
                <Eye size={14} /> View Sample Online
              </button>
            </motion.div>
          ))}
        </div>
      )}
      <motion.button
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.3 }}
        onClick={() => scrollToSection("#quote")}
        data-testid="samples-request-custom-button"
        className="mt-10 text-sm text-[#5E6E85] underline decoration-[#A07E1F]/40 underline-offset-4 transition-colors hover:text-[#A07E1F]"
      >
        Need a sample in your exact subject? Request one here →
      </motion.button>
      <SampleViewer sample={viewing} onClose={() => setViewing(null)} />
    </section>
  );
};
